import { useQueries, useQuery } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import type { ServiceKey } from '@shared/api/config';
import { key, STALE_TIME } from '@shared/api/query-keys';

import type { ConfigurationRow } from './types';

/**
 * One read per service — **nine independent queries, never one composite.**
 *
 * That is what gives the page its per-service error boundary: one service being
 * unreachable renders one section's banner rather than an empty page, and the
 * other eight stay usable. It is also why the fan-out is not merely a cost —
 * collapsing it into an aggregate route would take the boundary with it.
 *
 * The key is `['<service>', 'configurations']`, which is exactly the tuple every
 * mutation on this surface invalidates — and the tuple a vocabulary-bearing
 * change elsewhere in the console would invalidate too.
 */
export function configurationsKey(service: ServiceKey) {
  return key(service, 'configurations');
}

/** What a service actually puts in `data`, across the shapes the nine currently serve. */
type ConfigurationsPayload = unknown[] | { items?: unknown[] } | null;

/** One row as some service spells it, before this module settles on one spelling. */
interface WireConfigurationRow {
  key?: unknown;
  value?: unknown;
  description?: unknown;
  is_secret?: unknown;
  isSecret?: unknown;
  masked?: unknown;
  is_editable?: unknown;
  isEditable?: unknown;
  rotated_at?: unknown;
  updated_at?: unknown;
}

/**
 * Reads a configurations payload however the answering service shaped it.
 *
 * **This is an adapter over live divergence, not a preference.** The nine deployed
 * services do not agree on this route: six wrap the rows as `data.items` — which is
 * what `api-and-data-contracts.md` §5 fixes for every list on the platform — while one
 * returns a bare array; and the row itself arrives spelled `is_secret`, `isSecret` or
 * `masked` depending on which service answered. This surface reads all nine, so it is
 * the one place that divergence is visible, and reading only one of the shapes is why
 * six sections drew an empty table while reporting no error at all — worse than a
 * banner, because an empty settings table reads as «this service publishes nothing».
 *
 * The adapter is deliberately narrow and deliberately temporary: it normalises what is
 * there and invents nothing. When the services converge — or when `pnpm codegen` fills
 * `src/shared/api/generated/` from their OpenAPI documents — this function is what gets
 * deleted, and nothing else on the surface changes.
 */
export function toConfigurationRows(payload: ConfigurationsPayload): ConfigurationRow[] {
  const rows = Array.isArray(payload) ? payload : (payload?.items ?? []);

  return rows.map((raw) => {
    const row = raw as WireConfigurationRow;
    // `masked` is the same fact under a different name; an absent flag reads as
    // «not a secret», which is the only safe default for a *rendering* decision —
    // the value's presence, not this flag, is what actually withholds it.
    const isSecret = Boolean(row.is_secret ?? row.isSecret ?? row.masked ?? false);
    // A service that does not publish the flag has no write route this console can
    // reach, so the field is drawn closed rather than offered and then refused.
    const isEditable = Boolean(row.is_editable ?? row.isEditable ?? false);
    const rotatedAt = row.rotated_at ?? (isSecret ? row.updated_at : null) ?? null;

    return {
      key: String(row.key ?? ''),
      // Kept absent rather than nulled: the surface distinguishes «withheld
      // because secret» from «empty string», and a `null` here would erase that.
      ...(row.value === undefined || row.value === null ? {} : { value: String(row.value) }),
      description: String(row.description ?? ''),
      is_secret: isSecret,
      is_editable: isEditable,
      rotated_at: typeof rotatedAt === 'string' ? rotatedAt : null,
    };
  });
}

function configurationsQuery(service: ServiceKey) {
  return {
    queryKey: configurationsKey(service),
    queryFn: async () =>
      toConfigurationRows(await api.get<ConfigurationsPayload>(service, '/admin/configurations')),
    // A setting changes about once a quarter; a re-read per navigation would
    // spend nine requests on answers that have not moved.
    staleTime: STALE_TIME.vocabulary,
  };
}

export function useConfigurations(service: ServiceKey) {
  return useQuery(configurationsQuery(service));
}

/**
 * The two figures the contract notice states, **counted from what the services
 * returned** rather than written into the copy — a page whose own summary line
 * disagreed with the tables beneath it would be wrong about itself.
 *
 * It subscribes to the same nine query keys the sections do, so the cache
 * entries are shared and nothing is fetched twice: the count is a *reading* of
 * the page's own data, not a tenth request.
 */
export function useConfigurationCounts(services: readonly ServiceKey[]) {
  return useQueries({
    queries: services.map(configurationsQuery),
    combine: (results) => ({
      // A service that answered with rows is one that publishes a table; one
      // that failed or answered empty is not counted as publishing one.
      services: results.filter((result) => (result.data?.length ?? 0) > 0).length,
      keys: results.reduce((total, result) => total + (result.data?.length ?? 0), 0),
    }),
  });
}
